import { ExportType } from "@app/exports/enumerations";
import { CountyProjectContext, ProjectService } from "@app/projects/";
import { AdequacyRowStatusEnum, ImpactIndicator, NetworkAssessmentAnalysisType, ProjectPerspective, ProjectRunConfigurationStatus, ProjectRunConfigurationType, ProviderNetworkType, RegionSetStatusType } from "@app/projects/enumerators";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { AdequacyViewByAllTableData } from "@app/projects/results/adequacy";
import { AdequacyViewByAllComparisonResponse, AdequacyViewByAllResponse } from "@app/projects/results/adequacy";
import { RegionGranularity } from "@app/region-sets";
import { TooltipWrapper, formatDateCustom } from "@app/shared";
import { ColumnConfig, ColumnGroup, SortDirections, Waffles, WafflesFilterType } from "@app/shared/waffles";
import { ServicingAnalysisGranularityEnum, TemplateCategory, TemplateService } from "@app/templates";
import React, { FC, Fragment, JSX, useContext, useEffect, useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { SortingMetadata, WafflesPaginationProps } from "@app/shared/waffles/exports";
import { SpecialtyGroupExceptionStatus } from "../../results/common";
import InformationalSpecialtyGroupIndicator from "../InformationalSpecialtyGroupIndicator";
import SpecialtyExceptionTimelineLink from "../SpecialtyGroupExceptionTimelineLink";
import DetailsFilters from "./DetailsFilters";
import { cmsCreditDataAlert, cmsCreditIconToggleApply, cmsCreditIconToggleChanges } from "./CMSCreditComponents";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { GapFillStatusEnum } from "./";
import { XhrReadyStateEnum } from "@app/shared/xhrReadyStateEnum";
import { getFillableGapsIndicatorTooltip, getMarketAnalysisTooltip, showPatternOfCare } from "./utility";
import { UserContext } from "@app/user";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

function getServicingGranularityHeader(servicingGranularity: ServicingAnalysisGranularityEnum) {
  switch (servicingGranularity) {
    case ServicingAnalysisGranularityEnum.State:
      return "In State";
    case ServicingAnalysisGranularityEnum.Region:
    case ServicingAnalysisGranularityEnum.Unknown:
      return "In Region";
    case ServicingAnalysisGranularityEnum.County:
    default:
      return "In County";
  }
}

type ViewByAllProps = RouteComponentProps<unknown> & {
  exportComparedRunConfigurationId?: number;
  exportEndpoint: string;
};

const AdequacyViewByAll: FC<React.PropsWithChildren<ViewByAllProps>> = (props: ViewByAllProps): JSX.Element => {
  const projectContext = useContext(CountyProjectContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const user = useContext(UserContext);
  const gapFillStatusMap = useGapFillStatusDescriptions();
  const projectService = new ProjectService();
  let apiResponse: JQueryXHR;
  if (!projectContext.runConfiguration || projectContext.runConfiguration.status === ProjectRunConfigurationStatus.Failed) {
    return null;
  }

  const countyStatusFilterUndefined = useMemo(() => {
    return projectContext.adequacyFilters.active === undefined
      && projectContext.adequacyFilters.approved === undefined
      && projectContext.adequacyFilters.pending === undefined
      && projectContext.adequacyFilters.opportunity === undefined;
  }, [projectContext.adequacyFilters]);

  const [availableRegionStatuses, setAvailableRegionStatuses] = useState<RegionSetStatusType[]>([]);
  const [tableData, setTableData] = useState<AdequacyViewByAllTableData[]>([]);
  //const [tableFilters, setTableFilters] = useState<WafflesFilterGroup[]>([]);
  const [sortingMetadata, setSortingMetadata] = useState<SortingMetadata<AdequacyViewByAllTableData>>({
    sortDirection: SortDirections.None,
    sortedColumn: undefined,
  });
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasGaps, setHasGaps] = useState(false);
  const [hasExpandableSpecialties, setHasExpandableSpecialties] = useState(false);
  const [hasExceptions, setHasExceptions] = useState(false);
  const [hasChangedGaps, setHasChangedGaps] = useState(false);
  const [templateCategories, setTemplateCategories] = useState<TemplateCategory[]>([]);
  const [error, setError] = useState("");
  const [countyStatusActive, setCountyStatusActive] = useState(!!projectContext.adequacyFilters.active || countyStatusFilterUndefined);
  const [countyStatusApproved, setCountyStatusApproved] = useState(!!projectContext.adequacyFilters.approved || countyStatusFilterUndefined);
  const [countyStatusPending, setCountyStatusPending] = useState(!!projectContext.adequacyFilters.pending || countyStatusFilterUndefined);
  const [countyStatusOpportunity, setCountyStatusOpportunity] = useState(!!projectContext.adequacyFilters.opportunity);

  // These tend to come across as undefined, or switch from undefined to false, which changes
  // absolutely nothing, but causes a refresh of the component. This safeguards against that
  // useless API call.
  useEffect(() => {
    setCountyStatusActive(!!projectContext.adequacyFilters.active || countyStatusFilterUndefined);
    setCountyStatusApproved(!!projectContext.adequacyFilters.approved || countyStatusFilterUndefined);
    setCountyStatusPending(!!projectContext.adequacyFilters.pending || countyStatusFilterUndefined);
    setCountyStatusOpportunity(!!projectContext.adequacyFilters.opportunity);
  }, [
    projectContext.adequacyFilters.active,
    projectContext.adequacyFilters.pending,
    projectContext.adequacyFilters.approved,
    projectContext.adequacyFilters.opportunity,
  ]);

  // reset skip if necessary
  // these dependencies could each conceivably change the number of records,
  // and we don't want a case where our skip is 100 and we have 3 records
  useEffect(() => {
    setSkip(0);
  }, [
    projectContext.adequacyFilters,
    projectContext.compareToRunConfiguration,
    projectContext.regionFilters.state,
    projectContext.runConfiguration,
    projectContext.cmsCreditIsApplying, // can change the number of records in gaps view
    projectContext.cmsCreditShowOnlyChanges, // definitely changes the number of records
  ]);

  // use project container's loading/loadingResults when necessary
  useEffect(() => {
    setLoading(loading || projectContext.loading);
  }, [projectContext.loading]);

  const getDataFromEndpoint = async () => {
    // This conditional prevents extra api hits when the adequacyFilters have changed but the networkAssessmentAnalysisType hasn't yet
    if ((projectContext.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.Opportunity && !projectContext.adequacyFilters.opportunity)
      || projectContext.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ServiceArea && projectContext.adequacyFilters.opportunity) {
      return;
    }
    setError("");
    setLoading(true);
    const serviceAreaId = projectContext.regionFilters.state
      ? projectContext.runConfiguration.states().find((state) => state.regionCode === projectContext.regionFilters.state).regionSetId
      : projectContext.runConfiguration.serviceAreaRegionSetId;

    let topConfiguration: ProjectRunConfigurationResult;
    let bottomConfiguration: ProjectRunConfigurationResult;
    if (projectContext.compareToRunConfiguration) {
      if (projectContext.isDualMembershipMode) {
        topConfiguration = projectContext.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary ? projectContext.runConfiguration : projectContext.compareToRunConfiguration;
        bottomConfiguration = projectContext.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental ? projectContext.runConfiguration : projectContext.compareToRunConfiguration;
      } else {
        // if actual selected configuration is older, it goes on top. Otherwise the opposite.
        if ((projectContext.runConfiguration.dateCreated) < (projectContext.compareToRunConfiguration.dateCreated)) {
          topConfiguration = projectContext.runConfiguration;
          bottomConfiguration = projectContext.compareToRunConfiguration;
        } else {
          topConfiguration = projectContext.compareToRunConfiguration;
          bottomConfiguration = projectContext.runConfiguration;
        }
      }
    } else {
      topConfiguration = projectContext.runConfiguration;
    }

    abortApiCallIfExists();
    try {
      if (!!bottomConfiguration) {
        apiResponse = projectService.getAdequacyViewByAllComparisonTableModel(
          projectContext.project.projectId,
          topConfiguration.projectRunConfigurationId,
          projectContext.runConfiguration.networkAssessmentAnalysisType,
          serviceAreaId,
          bottomConfiguration.projectRunConfigurationId,
          projectContext.runFrequency,
          [], // would be tableFilters if they were in use
          sortingMetadata.sortDirection,
          sortingMetadata.sortedColumn,
          skip,
          take,
          projectContext.adequacyFilters,
        );
      } else {
        apiResponse = projectService.getAdequacyViewByAllTableModel(
          projectContext.project.projectId,
          topConfiguration.projectRunConfigurationId,
          projectContext.runConfiguration.networkAssessmentAnalysisType,
          serviceAreaId,
          projectContext.runFrequency,
          [], // would be tableFilters if they were in use
          sortingMetadata.sortDirection,
          sortingMetadata.sortedColumn,
          skip,
          take,
          projectContext.adequacyFilters,
          projectContext.cmsCreditIsApplying,
          projectContext.cmsCreditShowOnlyChanges,
        );
      }
      return await apiResponse;
    } catch {
      handleEndpointFailure(apiResponse);
    }
  };

  const abortApiCallIfExists = () => {
    if (!!(apiResponse)?.abort) {
      (apiResponse as JQueryXHR).abort();
    }
  }

  useEffect(() => {
    if (projectContext.compareToRunConfiguration) {
      getDataFromEndpoint().then(handleEndpointSuccess, handleEndpointFailure).finally(() => { apiResponse = null });
    }

    return () => {
      abortApiCallIfExists();
    }
  }, [projectContext.runFrequency]);

  // Update data
  useEffect(() => {
    // Avoid duplicate calls to getDataFromEndpoint if (1) we need the gap fill status descriptions, and
    // (2) we don't have the descriptions back from the API yet
    if (featureFlags.has(Features.GAP_FILL_STATUS) && gapFillStatusMap.gapFilled === '---') {
      return;
    }
    getDataFromEndpoint().then(handleEndpointSuccess, handleEndpointFailure).finally(() => { apiResponse = null; });

    return () => {
      abortApiCallIfExists();
    }
  }, [
    // we specifically don't care about adequacyFilters.viewby
    // project container tends to update adequacyfilters.viewby (the query string) in a separate update from the actual perspective object
    // which causes an extra hit to the api here
    countyStatusActive,
    countyStatusApproved,
    countyStatusOpportunity,
    countyStatusPending,
    skip,
    sortingMetadata,
    take,
    projectContext.adequacyFilters.all,
    projectContext.adequacyFilters.gaps,
    projectContext.adequacyFilters.exceptions,
    projectContext.adequacyFilters.changedGaps,
    projectContext.adequacyFilters.specialtyType,
    projectContext.cmsCreditIsApplying,
    projectContext.cmsCreditShowOnlyChanges,
    projectContext.compareToRunConfiguration,
    projectContext.regionFilters.state,
    projectContext.runConfiguration,
    gapFillStatusMap
  ]);

  const updatedStateElemMap = new Map<string, JSX.Element>();
  const updatedCountyElemMap = new Map<string, JSX.Element>();
  const impactIndicatorElemMap = new Map<string, JSX.Element>();

  const membershipJsx = (membershipName: string) => (
    <TooltipWrapper text={`Membership: ${membershipName}`}>
      <i className="fa fa-users" />
    </TooltipWrapper>
  );
  const linkJsx = (stateOrCounty: string, fullUrl: string) => (
    <Link
      key={stateOrCounty}
      to={{
        pathname: fullUrl,
        search: props.location.search,
      }}
    >
      {stateOrCounty}
    </Link>
  );

  const getUpdatedStateElem = (state: string, fullUrl: string, rowKey: string): JSX.Element => {
    if (!state) { return null; }
    if (updatedStateElemMap.has(rowKey)) { return updatedStateElemMap.get(rowKey); }

    updatedStateElemMap.set(rowKey, linkJsx(state, fullUrl));
    return updatedStateElemMap.get(rowKey);
  };

  const getUpdatedCountyElem = (county: string, fullUrl: string, rowKey: string): JSX.Element => {
    if (!county) { return null; }
    if (updatedCountyElemMap.has(rowKey)) { updatedCountyElemMap.get(rowKey); }

    updatedCountyElemMap.set(rowKey, linkJsx(county, fullUrl));
    return updatedCountyElemMap.get(rowKey);
  };

  const getImpactIndicatorElem = (impactIndicator: ImpactIndicator, metCriteria: boolean, rowKey: string): JSX.Element => {
    if (impactIndicatorElemMap.has(rowKey)) { return impactIndicatorElemMap.get(rowKey); }

    impactIndicatorElemMap.set(rowKey, getImpactIndicator(impactIndicator, metCriteria));
    return impactIndicatorElemMap.get(rowKey);
  };

  const handleEndpointSuccess = (response: AdequacyViewByAllResponse | AdequacyViewByAllComparisonResponse) => {
    const newTableData: AdequacyViewByAllTableData[] = [];
    const shoudShowIconFor = { exception: false, information: false };

    // in the event the adequacyviewbyall api call was aborted,
    // the Promise still "resolves" but response is undefined.
    // trying to access properties of undefined will throw an error,
    // which will be handled by catch{} below
    try {
      if (!response?.data) {
        throw new Error();
      }
      for (const adequacyViewByAllTableData of response.data) {
        if (!shoudShowIconFor.exception) {
          shoudShowIconFor.exception = shoudShowIconFor.exception || shouldShowExceptionHistory(adequacyViewByAllTableData.specialtyGroupExceptionStatus);
        }
        if (!shoudShowIconFor.information) {
          shoudShowIconFor.information = shoudShowIconFor.information || adequacyViewByAllTableData.isInformationalSpecialtyGroup;
        }
        if (shoudShowIconFor.exception && shoudShowIconFor.information) { break; }
      }

      response.data.forEach((adequacyTableData: AdequacyViewByAllTableData) => {
        const { key, state, county, countySSA, specialty, membershipName, prospectImpact, overallMet, stateAbbreviation, specialtyGroupExceptionStatus, isInformationalSpecialtyGroup, applicationCreditAccessMet, applicationCreditOverallMet, rowStatus, gapFillStatus, gapFillStatusUpdatedDate, gapFillStatusUpdatedByName } = adequacyTableData;
        // worst case here, the state is empty or wrong and the link works anyway but doesn't add a state filter
        const stateUrlFilter = !projectContext.regionFilters.state && !!stateAbbreviation ? `/state/${stateAbbreviation}` : "";
        const fullUrl = `${props.location.pathname}${stateUrlFilter}/specialty/${(specialty as string).split(" ")[0]}/county/${countySSA}`;
        const updatedState = getUpdatedStateElem(state as string, fullUrl, adequacyTableData.key);
        const updatedCounty = getUpdatedCountyElem(county as string, fullUrl, adequacyTableData.key);

        const updatedSpecialty = specialty ? (
          <Fragment key={specialty as string}>
            <InformationalSpecialtyGroupIndicator visible={shoudShowIconFor.information} isInformational={isInformationalSpecialtyGroup} />
            <SpecialtyExceptionTimelineLink
              visible={shoudShowIconFor.exception}
              showHistory={shouldShowExceptionHistory(specialtyGroupExceptionStatus)}
              projectId={projectContext.project.projectId}
              regionSetSpecialtyGroupId={parseInt(key, 10)}
            />
            <Link
              key={specialty as string}
              to={{
                pathname: fullUrl,
                search: props.location.search,
              }}
            >
              {specialty as string}
            </Link>
          </Fragment>
        ) : null;

        const updatedProspectImpact = prospectImpact ? getImpactIndicatorElem(prospectImpact as ImpactIndicator, overallMet === "Y", adequacyTableData.key) : null;

        const updatedAccessMet = projectContext.cmsCreditIsApplying ? applicationCreditAccessMet : adequacyTableData.accessMet;
        const updatedOverallMet = projectContext.cmsCreditIsApplying ? applicationCreditOverallMet : adequacyTableData.overallMet;
        let updatedRowStatus: AdequacyRowStatusEnum;
        if (projectContext.cmsCreditIsApplying && adequacyTableData.countyStatus === RegionSetStatusType.Pending) {
          if (applicationCreditOverallMet === "Y") {
            updatedRowStatus = AdequacyRowStatusEnum.NoGap;
          } else if (applicationCreditAccessMet === "Y") {
            updatedRowStatus = AdequacyRowStatusEnum.PartialGap;
          } else if (adequacyTableData.servicingMet === "Y") {
            updatedRowStatus = AdequacyRowStatusEnum.PartialGap;
          } else {
            updatedRowStatus = AdequacyRowStatusEnum.FullGap;
          }
        } else {
          updatedRowStatus = rowStatus;
        }

        const updatedGapFillStatus = gapFillStatusMap[gapFillStatus as GapFillStatusEnum] || gapFillStatus;

        newTableData.push({
          ...adequacyTableData,
          accessMet: updatedAccessMet,
          county: updatedCounty,
          gapFillStatus: updatedGapFillStatus,
          gapFillStatusUpdatedByName: gapFillStatusUpdatedByName,
          gapFillStatusUpdatedDate: gapFillStatusUpdatedDate ? formatDateCustom(gapFillStatusUpdatedDate, "MMM D 'YY hh:mm A") : '---',
          membershipName: membershipJsx(membershipName as string),
          overallMet: updatedOverallMet,
          prospectImpact: updatedProspectImpact,
          rowStatus: updatedRowStatus,
          specialty: updatedSpecialty,
          state: updatedState,
        });
      });

      setAvailableRegionStatuses(availableStatuses);
      setHasChangedGaps(response.hasOwnProperty("hasChangedGaps") && (response as AdequacyViewByAllComparisonResponse).hasChangedGaps);
      setHasGaps(response.hasGaps);
      setHasExpandableSpecialties(response.hasExpandableSpecialties);
      setHasExceptions(response.hasExceptions);
      setTableData(newTableData);
      setTotalRecords(response.totalRecords);
      setLoading(false);
      setError("");
    } catch {
      if (apiResponse?.readyState === XhrReadyStateEnum.Done) {
        setLoading(false);
        setError("There was an error retrieving the requested data.");
      }
      return;
    }
  };

  const handleEndpointFailure = (e: unknown) => {
    // only set loading to false if the apiResponse is in the complete state
    // this will avoid setting the error if there's a subsequent still-active request
    // after a failure or cancellation
    if (apiResponse?.readyState === XhrReadyStateEnum.Done) {
      setLoading(false);
      setError("There was an error retrieving the requested data.");
    }
    projectService.handleAbortConnection(e as JQueryXHR);
  };

  /* Get template categories for rowgroups
   * Requires: template service call, projectContext.templateMetadata (via project context)
   */
  useEffect(() => {
    const templateService = new TemplateService();
    templateService.getTemplateCategories(projectContext.templateMetadata.templateCollectionId, projectContext.templateMetadata.templateId).then((response) => {
      setTemplateCategories(response);
    });
  }, [projectContext.templateMetadata]);

  const isDistancePartOfTheTemplate = (): boolean => {
    return tableData.some(data =>
      data.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.Distance.toLowerCase() ||
      data.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeAndDistance.toLowerCase() ||
      data.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeOrDistance.toLowerCase()
    );
  };

  const isTimePartOfTheTemplate = (): boolean => {
    return tableData.some(data =>
      data.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.Time.toLowerCase() ||
      data.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeAndDistance.toLowerCase() ||
      data.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeOrDistance.toLowerCase()
    );
  };

  const getColumnConfig = useMemo(() => {
    const showAvgDist = projectContext.runConfiguration.showDistance && projectContext.project.showAverageDistance;
    const showMaxDist = projectContext.runConfiguration.showDistance && projectContext.project.showMaximumDistance;
    const showAvgTime = projectContext.runConfiguration.showTime && projectContext.project.showAverageTime;
    const showMaxTime = projectContext.runConfiguration.showTime && projectContext.project.showMaximumTime;
    const showWithTimeAndDistanceColumns = projectContext.runConfiguration.showWithTimeAndDistanceColumns;
    const hideInCompareMode = !!projectContext.compareToRunConfiguration;
    const hideTelehealth = projectContext.runConfiguration.telehealthSpecialtyGroupCodes.length <= 0 && (!projectContext.compareToRunConfiguration || projectContext.compareToRunConfiguration.telehealthSpecialtyGroupCodes.length <= 0);
    const hideCon = !projectContext.runConfiguration.hasConCredits || (projectContext.compareToRunConfiguration && !projectContext.compareToRunConfiguration.hasConCredits);
    const servicingGranularityHeader = getServicingGranularityHeader(projectContext.runConfiguration.template.servicingAnalysisGranularity);
    const hideGapFillStatus = !featureFlags.has(Features.GAP_FILL_STATUS);

    const showDistanceRequirements = isDistancePartOfTheTemplate();
    const showTimeRequirements = isTimePartOfTheTemplate();

    const columnConfig: ColumnConfig = {
      accessAvgDist: {
        displayText: projectContext.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1" : "Avg Dist",
        filterText: projectContext.templateMetadata.closestProviderCount > 1 ? "Access: Avg Dist 1" : "Access: Avg Dist",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist,
        sortOrder: 100,
        testId: "access-avg-dist",
      },
      accessAvgDist2: {
        displayText: "Avg Dist 2",
        filterText: "Access: Avg Dist 2",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist || projectContext.templateMetadata.closestProviderCount !== 2,
        sortOrder: 104,
        testId: "access-avg-dist-2",
      },
      accessAvgDist2With: {
        displayText: "Avg Dist 2 With Access",
        filterText: "Access: Avg Dist 2 With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist || projectContext.templateMetadata.closestProviderCount !== 2,
        sortOrder: 105,
        testId: "access-avg-dist2-with",
      },
      accessAvgDist2Without: {
        displayText: "Avg Dist 2 Without Access",
        filterText: "Access: Avg Dist 2 Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist || projectContext.templateMetadata.closestProviderCount !== 2,
        sortOrder: 106,
        testId: "access-avg-dist2-without",
      },
      accessAvgDistWith: {
        displayText: projectContext.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1 With Access" : "Avg Dist With Access",
        filterText: projectContext.templateMetadata.closestProviderCount > 1 ? "Access: Avg Dist 1 With Access" : "Access: Avg Dist With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist,
        sortOrder: 102,
        testId: "access-avg-dist-with",
      },
      accessAvgDistWithout: {
        displayText: projectContext.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1 Without Access" : "Avg Dist Without Access",
        filterText: projectContext.templateMetadata.closestProviderCount > 1 ? "Access: Avg Dist 1 Without Access" : "Access: Avg Dist Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist,
        sortOrder: 103,
        testId: "access-avg-dist-without",
      },
      accessAvgTime: {
        displayText: projectContext.templateMetadata.closestProviderCount > 1 ? "Avg Time 1" : "Avg Time",
        filterText: projectContext.templateMetadata.closestProviderCount > 1 ? "Access: Avg Time 1" : "Access: Avg Time",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime,
        sortOrder: 130,
        testId: "access-avg-time",
      },
      accessAvgTime2: {
        displayText: "Avg Time 2",
        filterText: "Access: Avg Time 2",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime || projectContext.templateMetadata.closestProviderCount !== 2,
        sortOrder: 134,
        testId: "access-avg-time-2",
      },
      accessAvgTime2With: {
        displayText: "Avg Time 2 With Access",
        filterText: "Access: Avg Time 2 With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime || projectContext.templateMetadata.closestProviderCount !== 2,
        sortOrder: 135,
        testId: "access-avg-time2-with",
      },
      accessAvgTime2Without: {
        displayText: "Avg Time 2 Without Access",
        filterText: "Access: Avg Time 2 Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime || projectContext.templateMetadata.closestProviderCount !== 2,
        sortOrder: 136,
        testId: "access-avg-time2-without",
      },
      accessAvgTimeWith: {
        displayText: projectContext.templateMetadata.closestProviderCount > 1 ? "Avg Time 1 With Access" : "Avg Time With Access",
        filterText: projectContext.templateMetadata.closestProviderCount > 1 ? "Access: Avg Time 1 With Access" : "Access: Avg Time With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime,
        sortOrder: 132,
        testId: "access-avg-time-with",
      },
      accessAvgTimeWithout: {
        displayText: projectContext.templateMetadata.closestProviderCount > 1 ? "Avg Time 1 Without Access" : "Avg Time Without Access",
        filterText: projectContext.templateMetadata.closestProviderCount > 1 ? "Access: Avg Time 1 Without Access" : "Access: Avg Time Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime,
        sortOrder: 133,
        testId: "access-avg-time-without",
      },
      accessCustomization: {
        displayText: "Access Customization",
        sortOrder: 66,
        testId: "access-customization",
      },
      accessMaxDist: {
        displayText: "Max Dist",
        filterText: "Access: Max Dist",
        filterType: WafflesFilterType.number,
        hidden: !showMaxDist,
        sortOrder: 107,
        testId: "access-max-dist",
      },
      accessMaxDistWith: {
        displayText: "Max Dist With Access",
        filterText: "Access: Max Dist With Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxDist,
        sortOrder: 108,
        testId: "access-max-dist-with",
      },
      accessMaxDistWithout: {
        displayText: "Max Dist Without Access",
        filterText: "Access: Max Dist Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxDist,
        sortOrder: 109,
        testId: "access-max-dist-without",
      },
      accessMaxTime: {
        displayText: "Max Time",
        filterText: "Access: Max Time",
        filterType: WafflesFilterType.number,
        hidden: !showMaxTime,
        sortOrder: 137,
        testId: "access-max-time",
      },
      accessMaxTimeWith: {
        displayText: "Max Time With Access",
        filterText: "Access: Max Time With Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxTime,
        sortOrder: 138,
        testId: "access-max-time-with",
      },
      accessMaxTimeWithout: {
        displayText: "Max Time Without Access",
        filterText: "Access: Max Time Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxTime,
        sortOrder: 139,
        testId: "access-max-time-without",
      },
      accessMet: {
        displayText: "Met",
        filterText: "Access: Met",
        filterType: WafflesFilterType.string,
        hidden: showWithTimeAndDistanceColumns,
        sortOrder: 180,
        testId: "access-met",
      },
      accessMetDist: {
        displayText: "Met Dist",
        filterText: "Access: Met Dist",
        filterType: WafflesFilterType.string,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        sortOrder: 160,
        testId: "access-met-dist",
      },
      accessMetTime: {
        displayText: "Met Time",
        filterText: "Access: Met Time",
        filterType: WafflesFilterType.string,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        sortOrder: 170,
        testId: "access-met-time",
      },
      accessTelehealthCredit: {
        displayText: "Telehealth %",
        filterText: "Access: Telehealth %",
        filterType: WafflesFilterType.number,
        hidden: hideTelehealth,
        sortOrder: 190,
        testId: "telehealth-credit",
      },
      accessWith: {
        displayText: "With %",
        filterText: "Access: With %",
        filterType: WafflesFilterType.number,
        sortOrder: 80,
        testId: "access-with-percent",
      },
      accessWithDist: {
        displayText: "With Dist %",
        filterText: "Access: With Dist %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        sortOrder: 110,
        testId: "access-with-dist",
      },
      accessWithTime: {
        displayText: "With Time %",
        filterText: "Access: With Time %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        sortOrder: 140,
        testId: "access-with-time-percent",
      },
      accessWithout: {
        displayText: "Without %",
        filterText: "Access: Without %",
        filterType: WafflesFilterType.number,
        sortOrder: 90,
        testId: "access-without-percent",
      },
      accessWithoutDist: {
        displayText: "Without Dist %",
        filterText: "Access: Without Dist %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        sortOrder: 120,
        testId: "access-without-dist",
      },
      accessWithoutTime: {
        displayText: "Without Time %",
        filterText: "Access: Without Time %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        sortOrder: 150,
        testId: "access-without-time",
      },
      attestationPercent: {
        displayText: "Attestation %",
        filterText: "Attestation %",
        filterType: WafflesFilterType.number,
        hidden: !projectContext.project.showAttestation,
        sortOrder: 320,
        testId: "attestation-percent",
      },
      conAccessCreditPercentage: {
        displayText: "CON %",
        filterText: "Access: CON %",
        filterType: WafflesFilterType.number,
        hidden: hideCon,
        sortOrder: 200,
        testId: "con-credit",
      },
      county: {
        displayText: "County",
        filterType: WafflesFilterType.string,
        sortOrder: 30,
        testId: "county",
      },
      countyClass: {
        displayText: "County Class",
        filterText: "County Class",
        filterType: WafflesFilterType.string,
        sortOrder: 43,
        testId: "county-class",
      },
      countySSA: {
        displayText: "County SSA",
        filterText: "County SSA",
        filterType: WafflesFilterType.string,
        sortOrder: 40,
        testId: "county-ssa",
      },
      countyStatus: {
        displayText: "County Status",
        filterText: "County Status",
        filterType: WafflesFilterType.string,
        sortOrder: 45,
        testId: "county-status",
      },
      fullMemberCount: {
        displayText: "Membership Count",
        sortOrder: 61,
        testId: "membership-count",
      },
      fullMemberCountWithAccess: {
        displayText: "# With",
        sortOrder: 75,
        testId: "membership-count-with-access",
      },
      fullMemberCountWithoutAccess: {
        displayText: "# Without",
        sortOrder: 85,
        testId: "membership-count-without-access",
      },
      gapFillStatus: {
        displayText: "Status",
        hidden: hideGapFillStatus,
        sortOrder: 325,
        testId: "gap-fill-status"
      },
      gapFillStatusUpdatedByName: {
        displayText: "Updated By",
        hidden: hideGapFillStatus,
        sortOrder: 340,
        testId: "gap-fill-status-updated-by-name"
      },
      gapFillStatusUpdatedDate: {
        displayText: "Last Updated",
        hidden: hideGapFillStatus,
        sortOrder: 330,
        testId: "gap-fill-status-last-updated"
      },
      isInformationalSpecialtyGroup: {
        displayText: "",
        hidden: true,
        testId: "",
      },
      marketAccess: {
        displayText: "Access %",
        filterText: "Market Analysis: Access %",
        filterType: WafflesFilterType.number,
        hidden: !projectContext.runConfiguration.marketProviderSetId,
        sortOrder: 270,
        testId: "access-percent",
      },
      marketDistinct: {
        displayText: "Servicing",
        filterText: "Market Analysis: Servicing",
        filterType: WafflesFilterType.number,
        hidden: !projectContext.runConfiguration.marketProviderSetId,
        sortOrder: 290,
        testId: "market-distinct",
      },
      marketFillableGapIndicator: {
        displayText: "Fillable Gap Indicator",
        hidden: !(projectContext.runConfiguration.useMarketProvidersForImpact || !!projectContext.runConfiguration.prospectProviderSetId) || projectContext.cmsCreditIsApplying,
        sortOrder: 323,
        testId: "market-fillable-gap-indicator",
        tooltip: getFillableGapsIndicatorTooltip(projectContext.runConfiguration),
      },
      marketPercentage: {
        displayText: "Market %",
        filterText: "Market Analysis: Market %",
        filterType: WafflesFilterType.number,
        hidden: !projectContext.runConfiguration.marketProviderSetId,
        sortOrder: 300,
        testId: "market-percentage",
      },
      marketProviderCount: {
        displayText: servicingGranularityHeader,
        filterText: `Market Analysis: ${servicingGranularityHeader}`,
        filterType: WafflesFilterType.number,
        hidden: !projectContext.runConfiguration.marketProviderSetId,
        sortOrder: 280,
        testId: "market-provider-count",
      },
      maxReqdDistance: {
        displayText: "Reqd Dist",
        hidden: !showDistanceRequirements,
        sortOrder: 63,
        testId: "max-reqd-distance",
      },
      maxReqdTime: {
        displayText: "Reqd Time",
        hidden: !showTimeRequirements,
        sortOrder: 64,
        testId: "max-reqd-time",
      },
      membershipName: {
        displayElement: <i className="fa fa-users" />,
        displayText: "Membership",
        hidden: (!tableData || tableData.every((r) => !r.membershipName)),
        noFilter: true,
        noSort: true,
        sortOrder: 60,
        testId: "membership-name",
      },
      minPassPercentage: {
        displayText: "Min Pass %",
        sortOrder: 65,
        testId: "min-pass-percentage",
      },
      minReqdProviderCount: {
        displayText: "Min Reqd Provs",
        sortOrder: 62,
        testId: "min-reqd-provider-count",
      },
      overallGapAge: {
        displayText: "Gap Age",
        filterText: "Overall: Gap Age",
        filterType: WafflesFilterType.number,
        hidden: !projectContext.project.includeGapAging,
        sortOrder: 260,
        testId: "overall-gap-age",
      },
      overallMet: {
        displayText: "Met",
        filterText: "Overall: Met",
        filterType: WafflesFilterType.string,
        sortOrder: 250,
        testId: "overall-met",
      },
      resultsFor: {
        displayText: "Results For",
        hidden: !projectContext.compareToRunConfiguration && !projectContext.isDualMembershipMode,
        noFilter: true,
        noSort: true,
        sortOrder: 70,
        testId: "results-for",
      },
      rowStatus: {
        displayText: "",
        hidden: true,
        testId: "",
      },
      servicingDistinct: {
        displayText: "Servicing",
        filterText: "Servicing Provider Requirements: Servicing",
        filterType: WafflesFilterType.number,
        sortOrder: 230,
        testId: "servicing-distinct",
      },
      servicingMet: {
        displayText: "Met",
        filterText: "Servicing Provider Requirements: Met",
        filterType: WafflesFilterType.string,
        sortOrder: 240,
        testId: "servicing-met",
      },
      servicingProviderCount: {
        displayText: servicingGranularityHeader,
        filterText: `Servicing Provider Requirements: ${getServicingGranularityHeader}`,
        filterType: WafflesFilterType.number,
        sortOrder: 220,
        testId: "servicing-provider-count",
      },
      servicingProviderToMemberRatio: {
        displayText: "Svcg Prov To Mbr Ratio",
        filterText: "Servicing Provider Requirements: Svcg Prov To Mbr Ratio",
        filterType: WafflesFilterType.number,
        sortOrder: 231,
        testId: "servicing-provider-to-member-ratio",
      },
      servicingRequired: {
        displayText: "Required",
        filterText: "Servicing Provider Requirements: Required",
        filterType: WafflesFilterType.number,
        sortOrder: 210,
        testId: "servicing-required",
      },
      specialty: {
        displayText: "Specialty Group",
        filterType: WafflesFilterType.string,
        sortOrder: 50,
        testId: "specialty-group",
      },
      specialtyGroupExceptionStatus: {
        displayText: "",
        hidden: true,
        testId: "",
      },
      state: {
        displayText: "State",
        filterType: WafflesFilterType.string,
        sortOrder: 20,
        testId: "state",
      },
      stateAbbreviation: {
        hidden: true,
        testId: "state-abbreviation",
      },
    };

    return columnConfig;
  }, [projectContext.runConfiguration, projectContext.compareToRunConfiguration, projectContext.project, tableData, projectContext.cmsCreditIsApplying]);

  const availableStatuses = useMemo(() => {
    return [...new Set(projectContext.runConfiguration.states().map((state) => {
      return state.subRegions.map((county) => {
        return projectContext.runConfiguration.getAvailableRegionStatusTypes(county);
      });
    }).reduce((acc, curr) => acc.concat(...curr), new Array<RegionSetStatusType>()))];
  }, [projectContext.runConfiguration]);

  const getRowColor = (row: AdequacyViewByAllTableData) => {
    let className = "";
    switch (row.rowStatus) {
      case AdequacyRowStatusEnum.NoGap:
        className = "success";
        break;
      case AdequacyRowStatusEnum.PartialGap:
        className = "warning";
        break;
      case AdequacyRowStatusEnum.FullGap:
        className = "danger";
        break;
      case AdequacyRowStatusEnum.ApprovedException:
        className = "info";
        break;
      case AdequacyRowStatusEnum.ZeroMembership:
        className = "gray success";
        break;
      case AdequacyRowStatusEnum.Empty:
        className = "gray danger";
      default:
        break;
    }

    if (row.isInformationalSpecialtyGroup) {
      className += " muted";
    }

    return className;
  };

  // since viewbyall's compare mode only uses the single data array,
  // we won't need to build a separate comparedRowGroups object
  const buildRowGroups = useMemo(() => {
    if (tableData.length === 0 || templateCategories.length === 0) {
      return null;
    }

    const rowGroups = new Map<string, string[]>();
    templateCategories.sort((a, b) => a.displayOrder - b.displayOrder);
    templateCategories.forEach((cat, index) => {
      const key = cat.displayName || `Unnamed Category ${index + 1}`;
      rowGroups.set(key, tableData
        .filter((r) => {
          return cat.specialtyGroupCodes
            // Not really a fan of this implementation
            .includes(((r.specialty as JSX.Element).key as string).split(" ")[0]);
        })
        .map((filteredRecord) => filteredRecord.key));
    });
    return rowGroups;
  }, [tableData, templateCategories]);

  const showNextClosestProviders = projectContext.runConfiguration && projectContext.runConfiguration.showNextClosestNetworkProviders || false;
  const availableExportTypes = (projectContext.project.showClosestProviderReport && !projectContext.compareToRunConfiguration)
    ? [ExportType.SpecialtySummaryByRegion, ExportType.ServicingProviders, ExportType.ClosestProvider]
    : [ExportType.SpecialtySummaryByRegion, ExportType.ServicingProviders];

  const enableMembershipTypeOptions = featureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT) &&
    projectContext.runConfiguration && [ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental].includes(projectContext.runConfiguration.projectRunConfigurationType);

  if (projectContext.runConfiguration.hasZipSummaries) {
    availableExportTypes.push(ExportType.ZipSummary);
  }
  const bulkActionBar = useMemo(() => {
    const selectedMemberships: number[] = [projectContext.runConfiguration.memberSetId];
    if (projectContext.isDualMembershipMode) {
      selectedMemberships.push(projectContext.compareToRunConfiguration.memberSetId);
    }
    return (
      <span className="bulk-action-group">
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: props.exportEndpoint,
            state: {
              activeModule: projectContext.module,
              adequacyFilters: projectContext.adequacyFilters,
              availableExportTypes: availableExportTypes,
              comparedRunConfigurationId: props.exportComparedRunConfigurationId,
              disableRegionFilters: false,
              disableSpecialtyGroupFilters: false,
              enableMembershipTypeOptions: enableMembershipTypeOptions,
              granularity: RegionGranularity.County,
              initialNetworkAssessmentAnalysisType: projectContext.runConfiguration.networkAssessmentAnalysisType,
              // values needed for export properties
              networkTypes: projectContext.runConfiguration && projectContext.runConfiguration.networkTypes as unknown as ProviderNetworkType[] || undefined,
              nextClosestProviders: showNextClosestProviders,
              perspective: ProjectPerspective.All,
              projectResourceId: projectContext.project.resource.resourceId,
              projectResourceName: projectContext.project.resource.name,
              projectURL: props.location.pathname + props.location.search,
              showAdvancedComparison: projectContext.project.showAdvancedComparison,
              showAppendNextClosestProviders: showNextClosestProviders,
              showAppendPatternOfCareProviders: showPatternOfCare(user, featureFlags, !!projectContext.compareToRunConfiguration, projectContext.project, projectContext.runConfiguration, undefined, hasExceptions, projectContext.cmsCreditIsApplying),
              showExpandableSpecialties: hasExpandableSpecialties,

              // new reportcustomization
              /* eslint-disable sort-keys */
              defaultReportType: projectContext.adequacyFilters.changedGaps ? ReportType.SpecialtySummaryByCounty : ReportType.AdequacyServicingProvider,
              projectName: projectContext.project.resource.name,
              networkAssessmentAnalysisType: projectContext.networkAssessmentAnalysisType,
              selectedRegionSetIds: [],
              filters: projectContext.filters,
              primaryMembership: { alias: projectContext.project.memberSetAlias || projectContext.project.memberSetName, id: projectContext.project.memberSetId },
              secondaryMembership: { alias: projectContext.project.memberSetAlias2 || projectContext.project.memberSetName2, id: projectContext.project.memberSetId2 },
              selectedMemberships,
              selectedTimeslice: projectContext.runConfiguration.projectRunConfigurationId, // runConfigurationId
              isCompareMode: !!projectContext.compareToRunConfiguration && !projectContext.isDualMembershipMode,
              selectedComparedTimeslice: !projectContext.isDualMembershipMode ? projectContext.compareToRunConfiguration?.projectRunConfigurationId : null, // compareToRunConfigurationId
              selectedChangedGapsType: projectContext.adequacyFilters.changedGaps,
              isViewingOpportunity: projectContext.adequacyFilters.opportunity,
              showAddedProviders: !!projectContext.compareToRunConfiguration,
              showMatchedProviders: !!projectContext.compareToRunConfiguration,
              showRemovedProviders: !!projectContext.compareToRunConfiguration,
              showPatternOfCare: projectContext.project.showPatternOfCareProviders,
              hasProspectProviders: !!projectContext.runConfiguration.prospectProviderSetId,
              hasMarketProviders: !!projectContext.runConfiguration.marketProviderSetId,
              hasNextClosestProviders: !!projectContext.runConfiguration.showNextClosestNetworkProviders,
              showZipSummariesReport: projectContext.project.hasZipSummaries,
              /* eslint-enable */
            },
          }}
          data-testid="adequacy-view-by-all__create-report-button"
        >
          <i className="fa fa-cloud-download" /> Create Report
        </Link>
        {!projectContext.compareToRunConfiguration && projectContext.project.showCMSCredit &&
          cmsCreditIconToggleApply(projectContext.cmsCreditIsApplying, projectContext.setCMSCreditIsApplying, projectContext.cmsCreditTemplateValue)}
        {!projectContext.compareToRunConfiguration && projectContext.project.showCMSCredit &&
          cmsCreditIconToggleChanges(projectContext.cmsCreditIsApplying, projectContext.cmsCreditShowOnlyChanges, projectContext.setCMSCreditShowOnlyChanges, projectContext.cmsCreditTemplateValue, projectContext.adequacyFilters.gaps, (Boolean)(projectContext.regionFilters.county) && (Boolean)(projectContext.regionFilters.specialty))}
      </span>
    );
  }, [
    projectContext.cmsCreditIsApplying,
    projectContext.cmsCreditShowOnlyChanges,
    projectContext.cmsCreditTemplateValue,
    props.exportEndpoint,
    props.location,
    projectContext.runConfiguration.networkAssessmentAnalysisType,
    projectContext.compareToRunConfiguration,
    projectContext.adequacyFilters,
    projectContext.isDualMembershipMode,
    projectContext.project,
    projectContext.runConfiguration,
    hasExpandableSpecialties
  ]);

  const customSort = (records: AdequacyViewByAllTableData[], column: keyof AdequacyViewByAllTableData, direction: SortDirections) => {
    setLoading(true);
    // setting those two state variables will cause the component to hit the API again, and update the table via props
    setSortingMetadata({
      sortDirection: direction,
      sortedColumn: column,
    });
    return records;
  };

  const detailsFilters = useMemo(() => {
    return (
      <DetailsFilters
        availableRegionFilters={availableRegionStatuses}
        enableGaps={hasGaps}
        enableExceptions={hasExceptions}
        enableChangedGaps={hasChangedGaps}
        showRegionStatusSelectors={projectContext.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ServiceArea}
      />
    );
  }, [
    availableRegionStatuses,
    hasGaps,
    hasChangedGaps,
    hasExceptions,
    projectContext.runConfiguration.networkAssessmentAnalysisType,
  ]);

  const handleChangePage = (pageNumber: number) => {
    setLoading(true);
    setSkip(Math.max((pageNumber - 1) * take, 0));
  };

  const handleChangeRecordsPerPage = (pageNumber: number, recordsPerPage: number) => {
    if (recordsPerPage !== take) {
      setLoading(true);
      setTake(recordsPerPage);
      setSkip(pageNumber - 1 * recordsPerPage);
    }
  };

  const getDescriptor = () => {
    if (loading) {
      return "Loading...";
    }
    if (error) {
      return error;
    }
    return `Displaying ${projectContext.compareToRunConfiguration ? tableData.length / 2 : tableData.length} of ${totalRecords} records`;
  };

  const paginationProps: WafflesPaginationProps = useMemo(() => {
    return {
      changePage: handleChangePage,
      changeRecordsPerPage: handleChangeRecordsPerPage,
      recordsPerPage: take,
      skip,
      totalRecords,
    };
  }, [take, skip, totalRecords]);

  return (
    <>
      {detailsFilters}
      {projectContext.cmsCreditIsApplying && cmsCreditDataAlert(projectContext.cmsCreditTemplateValue)}
      {getDescriptor()}
      {!error && (
        <Waffles<AdequacyViewByAllTableData>
          bulkActionBar={bulkActionBar}
          columnGroups={getColumnGroups(projectContext.runConfiguration)}
          columns={getColumnConfig}
          compareModeKeyColumns={3}
          customSort={customSort}
          data={tableData}
          enablePagination={true}
          fullWidth={true}
          loading={loading}
          paginationProps={paginationProps}
          passThroughSorting={true}
          preSortedCompareMode={!!projectContext.compareToRunConfiguration}
          rowDecorator={getRowColor}
          rowGroups={buildRowGroups}
          testId="adequacy-view-by-all"
        />
      )}
    </>
  );
};

export default AdequacyViewByAll;

function getImpactIndicator(impactIndicator: ImpactIndicator, metCriteria: boolean): JSX.Element {
  // no dot of any kind if the row already passes. ImpactIndicator is unreliable, at least for older results
  if (metCriteria) {
    return (<span key="0" />);
  }
  // impactIndicator comes capitalized from the API, so we need to lowercase the first letter
  switch (impactIndicator[0].toLowerCase() + impactIndicator.substring(1)) {
    case ImpactIndicator.EmptyDot:
      return (
        <TooltipWrapper tooltipWidth="150px" text="Single provider can increase member access percentage" key="1">
          <span className="text-warning" ><small><i className="fa fa-circle-o" /></small></span>
        </TooltipWrapper>
      );
    case ImpactIndicator.FullDot:
      return (
        <TooltipWrapper tooltipWidth="150px" text="Single provider can bring member access percentage to or above measurement threshold" key="2">
          <span className="text-success" ><small><i className="fa fa-circle" /></small></span>
        </TooltipWrapper>
      );
    case ImpactIndicator.None:
    default:
      return (<span key="0" />);
  }
}

// putting this down here because it has no dependencies on props or state
const getColumnGroups = (projectRunConfiguration: ProjectRunConfigurationResult): Array<ColumnGroup<AdequacyViewByAllTableData>> => {
  return [
    {
      children: ["state", "resultsFor", "county", "countyStatus", "countySSA", "countyClass", "specialty", "membershipName", "fullMemberCount"],
      hideKey: true,
      key: "id",
      testId: "group-id",
    },
    {
      children: ["minReqdProviderCount", "maxReqdDistance", "maxReqdTime", "minPassPercentage", "accessCustomization", "fullMemberCountWithAccess", "accessWith", "fullMemberCountWithoutAccess", "accessWithout", "accessAvgDist", "accessAvgDist2", "accessAvgDistWith", "accessAvgDist2With", "accessAvgDistWithout", "accessAvgDist2Without", "accessAvgTime", "accessAvgTimeWith", "accessAvgTime2With", "accessAvgTimeWithout", "accessAvgTime2Without", "accessMaxDist", "accessMaxDistWith", "accessMaxDistWithout", "accessWithDist", "accessWithoutDist", "accessAvgTime2", "accessMaxTime", "accessMaxTimeWith", "accessMaxTimeWithout", "accessWithTime", "accessWithoutTime", "accessMetDist", "accessMetTime", "accessMet", "accessTelehealthCredit", "conAccessCreditPercentage"],
      hideKey: false,
      key: "Access Requirements",
      testId: "group-access-requirements",
    },
    {
      children: ["servicingRequired", "servicingProviderCount", "servicingDistinct", "servicingProviderToMemberRatio", "servicingMet"],
      key: "Servicing Provider Requirements",
      testId: "group-servicing-requirements",
    },
    {
      children: ["overallMet", "overallGapAge"],
      key: "Overall",
      testId: "group-overall",
    },
    {
      children: ["marketAccess", "marketProviderCount", "marketDistinct", "marketPercentage"],
      key: "Market Analysis",
      showInfoIcon: true,
      testId: "group-market-analysis",
      tooltip: getMarketAnalysisTooltip(projectRunConfiguration)
    },
    {
      children: ["marketFillableGapIndicator", "gapFillStatus", "gapFillStatusUpdatedDate", "gapFillStatusUpdatedByName"],
      key: "Gap Resolution",
      testId: "group-gap-resolution",
    },
    {
      children: ["prospectImpact"],
      key: "Prospect",
      testId: "group-prospect",
    },
    {
      children: ["attestationPercent"],
      hideKey: true,
      key: "attestation",
      testId: "group-attestation",
    },
  ];
}

const shouldShowExceptionHistory = (specialtyGroupExceptionStatus: SpecialtyGroupExceptionStatus) => {
  return specialtyGroupExceptionStatus
    && specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.None
    && specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived;
};
